import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Button, Popconfirm, Skeleton, Space, Tooltip, Tag, List, Card } from "antd";
import { openNotificationWithIcon } from "../../utils/notificationUtils";
import {
  DeleteOutlined,
  CalendarOutlined,
  FileTextOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { getInvoicesByContestant } from "../../api/invoice";
import {
  getApplicationsByGroup,
  createApplication,
  deleteApplication,
  createRound2GroupApplications,
} from "../../api/application";
import { getAllGroupsPaymentStatus, updateGroupRound } from "../../api/group";
import { useSettings } from "../../contexts/settingsContext";
import { loadState } from "../../stores/store";
import { sendEligibilityNotification } from "../../api/user";
import GroupPaymentStatusModal from "./groupPaymentStatusModal";
import ApplyEventsModal from "../Applications/applyEventsModal";
import TeacherModal from "./teacherModal";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
};

const GroupTableComponent = ({
  groups,
  loading,
  openGroupMembersModal,
  deleteGroup,
  role,
  currentUserId,
  events,
  handleGroupApplicationsViewClick,
  handleRowClick,
  pagination,
  onGroupRoundUpdated,
}) => {
  const [groupPaymentStatus, setGroupPaymentStatus] = useState([]);
  const [isPaymentStatusModalVisible, setIsPaymentStatusModalVisible] =
    useState(false);
  const [isApplyEventsModalVisible, setIsApplyEventsModalVisible] =
    useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [previousSelectedEvents, setPreviousSelectedEvents] = useState([]);
  const [groupEventModalLoading, setGroupEventModalLoading] = useState(false);
  const [paymentStatusLoading, setPaymentStatusLoading] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [allGroupsPaymentStatus, setAllGroupsPaymentStatus] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [teacherModalVisible, setTeacherModalVisible] = useState(false);
  const [selectedTeacherGroup, setSelectedTeacherGroup] = useState(null);
  const [applications, setApplications] = useState([]);

  const [passingGroupId, setPassingGroupId] = useState(null);
  const [passedGroups, setPassedGroups] = useState(new Set());

  const isMobile = useIsMobile();
  const { state: settings } = useSettings();
  const auth = loadState();

  const refreshAllGroupsPaymentStatus = useCallback(async () => {
    try {
      const paymentStatuses = await getAllGroupsPaymentStatus();
      setAllGroupsPaymentStatus(paymentStatuses || []);
    } catch (error) {
      console.error("Error fetching payment status:", error);
    }
  }, []);

  useEffect(() => {
    const fetchInitialPaymentStatus = async () => {
      await refreshAllGroupsPaymentStatus();
      setDataLoading(false);
    };
    fetchInitialPaymentStatus();
  }, [settings.selectedRound, groups, refreshAllGroupsPaymentStatus]);

  const openTeacherModal = (group) => {
    setSelectedTeacherGroup(group);
    setTeacherModalVisible(true);
  };

  const fetchGroupPaymentStatus = async (group) => {
    try {
      setPaymentStatusLoading(true);
      const membersPaymentStatus = await Promise.all(
        group.group.map(async (member) => {
          const round = settings.selectedRound;
          const invoices = await getInvoicesByContestant(
            member.group_member._id,
            round
          );
          return {
            member: `${member.group_member.first_name} ${member.group_member.last_name}`,
            paid: invoices.every((invoice) => invoice.paid),
          };
        })
      );
      setGroupPaymentStatus(membersPaymentStatus);
      setIsPaymentStatusModalVisible(true);
    } catch (error) {
      console.error("Error fetching group payment status:", error);
    } finally {
      setPaymentStatusLoading(false);
    }
  };

  const handleApplyToEvents = async (group) => {
    setSelectedGroup(group);
    try {
      setGroupEventModalLoading(true);
      const groupApplications = await getApplicationsByGroup(group._id);
      setApplications(groupApplications || []);

      const eventsForRound = (groupApplications || [])
        .filter((app) => app.round === group.round)
        .map((app) => app.event);

      setPreviousSelectedEvents(eventsForRound);
      setSelectedEvents(eventsForRound);
      setIsApplyEventsModalVisible(true);
    } catch (error) {
      if (String(error?.message || "").includes("404")) {
        setPreviousSelectedEvents([]);
        setSelectedEvents([]);
        setIsApplyEventsModalVisible(true);
      } else {
        console.error("Error fetching group applications:", error);
      }
    } finally {
      setGroupEventModalLoading(false);
    }
  };

  const handlePassGroup = async (group) => {
    setPassingGroupId(group._id);

    try {
      await updateGroupRound(group._id, settings.selectedRound);

      if (settings.selectedRound === 1 && group.teacher_email) {
        const memberNames = Array.isArray(group.group)
          ? group.group
              .map((member) => {
                const memberObj = member.group_member || member;

                const firstName = memberObj.first_name || memberObj.name || "";
                const lastName = memberObj.last_name || "";
                const fullName = `${firstName} ${lastName}`.trim();

                return fullName || null;
              })
              .filter(Boolean)
          : [];

        await sendEligibilityNotification(
          group.teacher_name || group.group_name,
          group.teacher_email,
          memberNames
        );
      }

      setPassedGroups((prev) => {
        const next = new Set(prev);
        next.add(group._id);
        return next;
      });

      try {
        await onGroupRoundUpdated?.();
      } catch (refreshError) {
        console.error("Error refreshing groups after passing group:", refreshError);
      }

      openNotificationWithIcon(
        "success",
        "Success",
        "Successfully moved group to next round"
      );
    } catch (e) {
      openNotificationWithIcon("error", "Error", e.message);
    } finally {
      setPassingGroupId(null);
    }
  };

  const handleResendEligibility = useCallback(async (group) => {
    if (!group || !group.teacher_email) {
      openNotificationWithIcon(
        "error",
        "Error",
        "No teacher email is available for this group."
      );
      return;
    }

    try {
      const memberNames = Array.isArray(group.group)
        ? group.group
            .map((member) => {
              const memberObj = member.group_member || member;

              const firstName = memberObj.first_name || memberObj.name || "";
              const lastName = memberObj.last_name || "";
              const fullName = `${firstName} ${lastName}`.trim();

              return fullName || null;
            })
            .filter(Boolean)
        : [];

      await sendEligibilityNotification(
        group.teacher_name || group.group_name,
        group.teacher_email,
        memberNames
      );

      openNotificationWithIcon(
        "success",
        "Email Sent",
        "Eligibility notification has been re-sent to the teacher."
      );
    } catch (error) {
      console.error("Error re-sending eligibility notification:", error);
      openNotificationWithIcon(
        "error",
        "Error",
        error.message || "Failed to re-send eligibility notification."
      );
    }
  }, []);

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvents((prevSelectedEvents) => {
      const isSelected = prevSelectedEvents.some((e) => e._id === event._id);
      if (isSelected) {
        return prevSelectedEvents.filter((e) => e._id !== event._id);
      }
      return [...prevSelectedEvents, event];
    });
  }, []);

  const handleApply = async () => {
    if (!selectedGroup) return;
    try {
      setIsApplying(true);

      if (!settings.singleRound && selectedGroup.round === 1) {
        const maxEvents = selectedGroup.group_size < 10 ? 4 : 2;
        if (selectedEvents.length > maxEvents) {
          throw new Error(
            `You can only apply this group to a maximum of ${maxEvents} events for round 1.`
          );
        }
      }

      const groupApplications = await getApplicationsByGroup(selectedGroup._id);
      const existingEventIds = (groupApplications || [])
        .filter((app) => app.round === selectedGroup.round)
        .map((app) => app.event._id);

      const newApplications = selectedEvents
        .filter((event) => !existingEventIds.includes(event._id))
        .map((event) => ({
          event: event._id,
          group: selectedGroup._id,
          round: selectedGroup.round,
          province: selectedGroup.province,
        }));

      if (selectedGroup.round === 2) {
        await Promise.all(
          newApplications.map((appData) =>
            createRound2GroupApplications({
              event: appData.event,
              group: appData.group,
              province: appData.province,
            })
          )
        );
      } else {
        await Promise.all(
          newApplications.map((application) => createApplication(application))
        );
      }

      const eventsToDelete = previousSelectedEvents
        .filter(
          (event) => !selectedEvents.some((e) => e._id === event._id)
        )
        .map(
          (event) =>
            (groupApplications || []).find(
              (app) => app.event._id === event._id
            )?._id
        )
        .filter(Boolean);

      await Promise.all(
        eventsToDelete.map((applicationId) =>
          deleteApplication(applicationId)
        )
      );

      setIsApplyEventsModalVisible(false);

      await refreshAllGroupsPaymentStatus();

      openNotificationWithIcon(
        "success",
        "Applications Updated",
        "The group has been successfully applied to the selected events."
      );
    } catch (error) {
      console.error("Error creating or deleting applications:", error);
      openNotificationWithIcon(
        "error",
        "Error",
        `Error updating applications: ${error.message}`
      );
    } finally {
      setIsApplying(false);
    }
  };

  const getRoundTag = (round) => {
    if (round === 1) return <Tag color="blue">Round 1</Tag>;
    if (round === 2) return <Tag color="green">Round 2</Tag>;
    if (round === 3) return <Tag color="purple">Round 3</Tag>;
    return <Tag color="default">-</Tag>;
  };

  const renderMobileActions = (record) => {
    const canPass =
      record.round === settings.selectedRound &&
      role === "admin" &&
      !passedGroups.has(record._id) &&
      !settings.singleRound;
    const payAndApplyDisabled =
      record.round === 2 && !settings.nationalApplicationsOpen;

    if (role === "contestant" && record.created_by !== currentUserId) {
      return null;
    }

    if (role === "contestant") {
      return (
        <Space wrap size={[6, 6]}>
          <Button size="small" onClick={() => handleGroupApplicationsViewClick(record)} icon={<CalendarOutlined />}>
            Events
          </Button>
          <Button size="small" type="primary" onClick={() => handleApplyToEvents(record)}>
            Apply to Events
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this group?"
            onConfirm={() => deleteGroup(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete Group">
              <Button danger type="text" shape="circle" size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      );
    }

    return (
      <Space wrap size={[6, 6]}>
        <Button size="small" onClick={() => handleGroupApplicationsViewClick(record)} icon={<CalendarOutlined />}>
          Events
        </Button>
        {!payAndApplyDisabled && (
          <Button size="small" onClick={() => handleRowClick(record._id)} icon={<FileTextOutlined />}>
            Pay Here
          </Button>
        )}
        {!payAndApplyDisabled && (
          <Button size="small" type="primary" onClick={() => handleApplyToEvents(record)}>
            Apply to Events
          </Button>
        )}
        {canPass && (
          <Button
            size="small"
            type="primary"
            onClick={() => handlePassGroup(record)}
            loading={passingGroupId === record._id}
          >
            Pass Group
          </Button>
        )}
        {role === "admin" && record.round === 2 && (
          <Button size="small" onClick={() => handleResendEligibility(record)} icon={<MailOutlined />}>
            Resend Email
          </Button>
        )}
        <Popconfirm
          title="Are you sure you want to delete this group?"
          onConfirm={() => deleteGroup(record._id)}
          okText="Yes"
          cancelText="No"
        >
          <Tooltip title="Delete Group">
            <Button danger type="text" shape="circle" size="small" icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      </Space>
    );
  };

  const getPaymentStatusTag = (groupId) => {
    const status = allGroupsPaymentStatus.find(
      (statusItem) => statusItem.groupId === groupId
    );
    if (status && status.isPaid) {
      return <Tag color="green">Paid</Tag>;
    }
    return <Tag color="red">Not Paid</Tag>;
  };

  const columns = useMemo(
    () => [
      {
        title: "Round",
        dataIndex: "round",
        key: "round",
        render: (text, record) => {
          let color = "default";
          let statusText = "-";

          if (record.round === 1) {
            color = "blue";
            statusText = "Round 1";
          } else if (record.round === 2) {
            color = "green";
            statusText = "Round 2";
          } else if (record.round === 3) {
            color = "purple";
            statusText = "Round 3";
          }

          return <Tag color={color}>{statusText}</Tag>;
        },
      },
      {
        title: "Group Number",
        dataIndex: "group_number",
        key: "group_number",
        responsive: ["lg"],
      },
      {
        title: "Group Name",
        dataIndex: "group_name",
        key: "group_name",
        render: (text, record) => (
          <a onClick={() => openGroupMembersModal(record)}>{text}</a>
        ),
      },
      {
        title: "Teacher Name",
        dataIndex: "teacher_name",
        key: "teacher_name",
        responsive: ["lg"],
        render: (text, record) => (
          <a
            onClick={(e) => {
              e.stopPropagation();
              openTeacherModal(record);
            }}
          >
            {text}
          </a>
        ),
      },
      {
        title: "Group Type",
        dataIndex: "group_type",
        key: "group_type",
        responsive: ["lg"],
      },
      {
        title: "Payment Status",
        key: "payment_status",
        render: (text, record) => (
          <Space>
            <Tooltip title="Click to view detailed payment status">
              <span
                style={{ cursor: "pointer" }}
                onClick={() => fetchGroupPaymentStatus(record)}
              >
                {getPaymentStatusTag(record._id)}
              </span>
            </Tooltip>
          </Space>
        ),
      },
      {
        title: "Action",
        key: "action",
        render: (text, record) => {
          const canPass =
            record.round === settings.selectedRound &&
            role === "admin" &&
            !passedGroups.has(record._id) &&
            !settings.singleRound;

          const payAndApplyDisabled =
            record.round === 2 && !settings.nationalApplicationsOpen;

          const commonActions = (
            <>
              <Button
                onClick={() => handleGroupApplicationsViewClick(record)}
                icon={<CalendarOutlined />}
              >
                Events
              </Button>

              {!payAndApplyDisabled && (
                <Button
                  onClick={() => handleRowClick(record._id)}
                  icon={<FileTextOutlined />}
                >
                  Pay Here
                </Button>
              )}

              {!payAndApplyDisabled && (
                <Button
                  type="primary"
                  onClick={() => handleApplyToEvents(record)}
                >
                  Apply to Events
                </Button>
              )}

              {canPass && (
                <Button
                  type="primary"
                  onClick={() => handlePassGroup(record)}
                  loading={passingGroupId === record._id}
                >
                  Pass Group
                </Button>
              )}

              {role === "admin" && record.round === 2 && (
                <Button
                  onClick={() => handleResendEligibility(record)}
                  icon={<MailOutlined />}
                >
                  Resend Email
                </Button>
              )}

              <Popconfirm
                title="Are you sure you want to delete this group?"
                onConfirm={() => deleteGroup(record._id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete Group">
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    type="text"
                    shape="circle"
                  />
                </Tooltip>
              </Popconfirm>
            </>
          );

          if (role !== "contestant") {
            return <Space>{commonActions}</Space>;
          }

          if (role === "contestant" && record.created_by === currentUserId) {
            return (
              <Space>
                <Button
                  onClick={() => handleGroupApplicationsViewClick(record)}
                  icon={<CalendarOutlined />}
                >
                  Events
                </Button>
                <Button onClick={() => handleApplyToEvents(record)}>
                  Apply to Events
                </Button>
                <Popconfirm
                  title="Are you sure you want to delete this group?"
                  onConfirm={() => deleteGroup(record._id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Delete Group">
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      type="text"
                      shape="circle"
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            );
          }

          return null;
        },
      },
    ],
    [
      openGroupMembersModal,
      openTeacherModal,
      fetchGroupPaymentStatus,
      handleGroupApplicationsViewClick,
      handleRowClick,
      handleApplyToEvents,
      handlePassGroup,
      handleResendEligibility,
      deleteGroup,
      settings.selectedRound,
      settings.nationalApplicationsOpen,
      role,
      currentUserId,
      passedGroups,
      passingGroupId,
      allGroupsPaymentStatus,
    ]
  );

  if (loading || dataLoading) {
    return <Skeleton active />;
  }

  return (
    <>
      {isMobile ? (
        <List
          dataSource={groups}
          pagination={pagination}
          rowKey="_id"
          renderItem={(record) => (
            <List.Item style={{ padding: "8px 0", display: "block" }}>
              <Card size="small" className="group-mobile-card">
                <div className="group-card-header">
                  <div className="group-card-tags">
                    {getRoundTag(record.round)}
                    <Tooltip title="Click to view detailed payment status">
                      <span
                        style={{ cursor: "pointer" }}
                        onClick={() => fetchGroupPaymentStatus(record)}
                      >
                        {getPaymentStatusTag(record._id)}
                      </span>
                    </Tooltip>
                  </div>
                  {record.group_number && (
                    <span className="group-card-number">#{record.group_number}</span>
                  )}
                </div>
                <div
                  className="group-card-name"
                  onClick={() => openGroupMembersModal(record)}
                >
                  {record.group_name}
                </div>
                {record.teacher_name && (
                  <div
                    className="group-card-teacher"
                    onClick={(e) => { e.stopPropagation(); openTeacherModal(record); }}
                  >
                    {record.teacher_name}
                  </div>
                )}
                {record.group_type && (
                  <div className="group-card-type">{record.group_type}</div>
                )}
                <div className="group-card-actions">
                  {renderMobileActions(record)}
                </div>
              </Card>
            </List.Item>
          )}
        />
      ) : (
        <Table
          dataSource={groups}
          columns={columns}
          loading={loading}
          rowKey="_id"
          scroll={{ x: true }}
          pagination={pagination}
        />
      )}

      <GroupPaymentStatusModal
        isVisible={paymentStatusLoading || isPaymentStatusModalVisible}
        onClose={() => setIsPaymentStatusModalVisible(false)}
        groupPaymentStatus={groupPaymentStatus}
        loading={paymentStatusLoading}
      />

      <ApplyEventsModal
        visible={groupEventModalLoading || isApplyEventsModalVisible}
        onClose={() => setIsApplyEventsModalVisible(false)}
        events={events}
        loading={groupEventModalLoading}
        openPopup={() => {}}
        userRole={role}
        confirmLoading={isApplying}
        handleDeleteEvent={() => {}}
        onSelectEvent={handleSelectEvent}
        selectedEvents={selectedEvents}
        onApply={handleApply}
        source="group"
        group={selectedGroup}
        applications={applications}
      />

      <TeacherModal
        visible={teacherModalVisible}
        onClose={() => setTeacherModalVisible(false)}
        group={selectedTeacherGroup}
      />
    </>
  );
};

const GroupTable = React.memo(GroupTableComponent);

export default GroupTable;
